import React, { useState } from 'react';
import CyberBadge from '../common/CyberBadge';
import { Users, Lock, Unlock, Search, Filter, Eye, X, UserCheck, Shield } from 'lucide-react';

export default function UsersView({ users = [], onBlockUser, onUnblockUser }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);

  // Filtered users
  const filteredUsers = users.filter((u) => {
    const term = searchTerm.trim().toLowerCase();
    const nameMatch = (u.fullName || u.name || '').toLowerCase().includes(term);
    const emailMatch = (u.email || '').toLowerCase().includes(term);
    const phoneMatch = (u.phone || '').includes(term);
    const cityMatch = (u.city || '').toLowerCase().includes(term);
    const matchesSearch = !term || nameMatch || emailMatch || phoneMatch || cityMatch;

    const matchesRole = roleFilter === 'all' || (u.role && u.role.toLowerCase() === roleFilter.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && !u.isBlocked) ||
      (statusFilter === 'blocked' && u.isBlocked);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const totalUsers = users.length;
  const activeCount = users.filter((u) => !u.isBlocked).length;
  const blockedCount = users.filter((u) => u.isBlocked).length;
  const captainRoleCount = users.filter((u) => u.role === 'CAPTAIN' || u.role === 'captain').length;
  const artistRoleCount = users.filter((u) => u.role === 'ARTIST' || u.role === 'artist').length;

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header & Metrics */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={28} style={{ color: 'var(--neon-cyan)' }} /> Platform User Directory ({filteredUsers.length} of {totalUsers})
          </h1>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Comprehensive management of user accounts, roles, access permissions, security suspensions, and profiles.
          </p>
        </div>
      </div>

      {/* Metrics Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
        <div className="cyber-card" style={{ padding: '14px 18px', borderLeft: '4px solid var(--neon-cyan)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>TOTAL ACCOUNTS</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-primary)', marginTop: '2px' }}>{totalUsers}</div>
        </div>
        <div className="cyber-card" style={{ padding: '14px 18px', borderLeft: '4px solid var(--neon-green)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>ACTIVE USERS</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--neon-green)', marginTop: '2px' }}>{activeCount}</div>
        </div>
        <div className="cyber-card" style={{ padding: '14px 18px', borderLeft: '4px solid var(--neon-purple)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>CAPTAINS / HOSTS</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--neon-purple)', marginTop: '2px' }}>{captainRoleCount}</div>
        </div>
        <div className="cyber-card" style={{ padding: '14px 18px', borderLeft: '4px solid var(--neon-amber)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>ARTISTS & PRODUCERS</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--neon-amber)', marginTop: '2px' }}>{artistRoleCount}</div>
        </div>
        <div className="cyber-card" style={{ padding: '14px 18px', borderLeft: '4px solid var(--neon-rose)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>SUSPENDED</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--neon-rose)', marginTop: '2px' }}>{blockedCount}</div>
        </div>
      </div>

      {/* Search & Filter Controls */}
      <div className="cyber-card" style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '240px' }}>
          <div className="search-input-wrapper" style={{ width: '100%' }}>
            <input
              type="text"
              placeholder="Search by name, email, phone, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '10px 14px 10px 36px', borderRadius: '8px', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border-color)', color: '#FFF' }}
            />
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Role Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border-color)', padding: '6px 12px', borderRadius: '8px' }}>
            <Filter size={13} style={{ color: 'var(--neon-cyan)' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>ROLE:</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: '#FFF', fontSize: '0.8rem', fontWeight: 700, outline: 'none', cursor: 'pointer' }}
            >
              <option value="all" style={{ background: 'var(--bg-surface)' }}>All Roles</option>
              <option value="user" style={{ background: 'var(--bg-surface)' }}>User</option>
              <option value="captain" style={{ background: 'var(--bg-surface)' }}>Captain</option>
              <option value="artist" style={{ background: 'var(--bg-surface)' }}>Artist</option>
              <option value="admin" style={{ background: 'var(--bg-surface)' }}>Admin</option>
            </select>
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border-color)', padding: '6px 12px', borderRadius: '8px' }}>
            <Shield size={13} style={{ color: 'var(--neon-green)' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800 }}>STATUS:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: '#FFF', fontSize: '0.8rem', fontWeight: 700, outline: 'none', cursor: 'pointer' }}
            >
              <option value="all" style={{ background: 'var(--bg-surface)' }}>All Status</option>
              <option value="active" style={{ background: 'var(--bg-surface)' }}>Active</option>
              <option value="blocked" style={{ background: 'var(--bg-surface)' }}>Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="cyber-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div className="table-container" style={{ border: 'none' }}>
          <table className="table">
            <thead>
              <tr>
                <th>USER NAME</th>
                <th>EMAIL / CONTACT</th>
                <th>ROLE</th>
                <th>CITY</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    No registered user accounts matching "{searchTerm}".
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u._id}>
                    <td style={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: u.isBlocked ? 'rgba(255, 51, 102, 0.2)' : 'rgba(0, 240, 255, 0.15)',
                            border: `1px solid ${u.isBlocked ? 'var(--neon-rose)' : 'var(--neon-cyan)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: u.isBlocked ? 'var(--neon-rose)' : 'var(--neon-cyan)',
                            fontWeight: 900,
                            fontSize: '0.8rem',
                          }}
                        >
                          {(u.fullName || u.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div>{u.fullName || u.name || 'Platform User'}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            ID: {u._id ? `${u._id.slice(0, 8)}...` : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>{u.email || 'N/A'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.phone || 'No phone'}</div>
                    </td>
                    <td>
                      <span className={`badge ${u.role === 'ADMIN' ? 'badge-rose' : u.role === 'CAPTAIN' ? 'badge-purple' : u.role === 'ARTIST' ? 'badge-amber' : 'badge-cyan'}`}>
                        {u.role || 'USER'}
                      </span>
                    </td>
                    <td>{u.city || 'Indore'}</td>
                    <td>
                      <CyberBadge status={u.isBlocked ? 'blocked' : 'active'} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          className="btn btn-outline"
                          style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                          onClick={() => setSelectedUser(u)}
                          title="Inspect full user profile & permissions"
                        >
                          <Eye size={13} /> Inspect
                        </button>
                        {u.isBlocked ? (
                          <button className="btn btn-emerald" style={{ padding: '6px 12px', fontSize: '0.75rem' }} onClick={() => onUnblockUser(u._id)}>
                            <Unlock size={13} /> Unblock
                          </button>
                        ) : (
                          <button className="btn btn-danger" style={{ padding: '6px 12px', fontSize: '0.75rem' }} onClick={() => onBlockUser(u._id)}>
                            <Lock size={13} /> Suspend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Details Inspection Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content animate-scale" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <UserCheck size={22} style={{ color: 'var(--neon-cyan)' }} />
                <h3 style={{ fontSize: '1.2rem', margin: 0, color: 'var(--text-primary)' }}>User Account Details</h3>
              </div>
              <button onClick={() => setSelectedUser(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, var(--neon-cyan), var(--neon-purple))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#000',
                    fontWeight: 900,
                    fontSize: '1.2rem',
                  }}
                >
                  {(selectedUser.fullName || selectedUser.name || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#FFF' }}>{selectedUser.fullName || selectedUser.name || 'Platform User'}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>ID: {selectedUser._id}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 800 }}>EMAIL ADDRESS</div>
                  <div style={{ fontSize: '0.88rem', color: '#FFF', marginTop: '2px' }}>{selectedUser.email || 'N/A'}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 800 }}>PHONE NUMBER</div>
                  <div style={{ fontSize: '0.88rem', color: '#FFF', marginTop: '2px' }}>{selectedUser.phone || 'N/A'}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 800 }}>LOCATION / CITY</div>
                  <div style={{ fontSize: '0.88rem', color: '#FFF', marginTop: '2px' }}>{selectedUser.city || 'Indore'}</div>
                </div>
                <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 800 }}>ACCOUNT STATUS</div>
                  <div style={{ fontSize: '0.88rem', color: selectedUser.isBlocked ? 'var(--neon-rose)' : 'var(--neon-green)', fontWeight: 800, marginTop: '2px' }}>
                    {selectedUser.isBlocked ? 'SUSPENDED' : 'ACTIVE & VERIFIED'}
                  </div>
                </div>
              </div>

              <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 800 }}>CURRENT ASSIGNED ROLE</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--neon-purple)', fontWeight: 800, marginTop: '2px' }}>
                  {selectedUser.role || 'USER'} (Full Platform Access)
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                {selectedUser.isBlocked ? (
                  <button
                    className="btn btn-emerald"
                    style={{ flex: 1 }}
                    onClick={() => {
                      onUnblockUser(selectedUser._id);
                      setSelectedUser({ ...selectedUser, isBlocked: false });
                    }}
                  >
                    <Unlock size={15} /> Restore Account Access
                  </button>
                ) : (
                  <button
                    className="btn btn-danger"
                    style={{ flex: 1 }}
                    onClick={() => {
                      onBlockUser(selectedUser._id);
                      setSelectedUser({ ...selectedUser, isBlocked: true });
                    }}
                  >
                    <Lock size={15} /> Suspend / Block User
                  </button>
                )}
                <button className="btn btn-outline" onClick={() => setSelectedUser(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
